import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import MainLayout from './layouts/MainLayout';
import CategoryReportsPage from './pages/CategoryReportsPage';
import DatabaseManagementPage from './pages/DatabaseManagementPage';
import HertzAutoPage from './pages/HertzAutoPage';
import MotorqPage from './pages/MotorqPage';
import VinInventoryPage from './pages/VinInventoryPage';
import FleetIntelligencePage from './pages/FleetIntelligencePage';
import NmacJourneysPage from './pages/NmacJourneysPage';
import CocoPage from './pages/CocoPage';
import {
  ReportGroupsProvider,
  useReportGroups,
} from './context/ReportGroupsContext';
import { CocoProvider } from './context/CocoContext';
import LoginPage from './pages/LoginPage';
import { AuthProvider, useAuth } from './context/AuthContext';

const DefaultCategoryRedirect: React.FC = () => {
  const { loading, reportGroups } = useReportGroups();

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="calc(100vh - 120px)"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to the first available category
  const firstCategory = reportGroups.length > 0 ? reportGroups[0].id : 'btr';
  return <Navigate to={`/${firstCategory}`} replace />;
};

const RequireAuth: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

const ReportGroupsLayout: React.FC = () => (
  <ReportGroupsProvider>
    <CocoProvider>
      <Outlet />
    </CocoProvider>
  </ReportGroupsProvider>
);

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />

    <Route element={<RequireAuth />}>
      <Route element={<ReportGroupsLayout />}>
        <Route path="/" element={<MainLayout />}>
          <Route index element={<DefaultCategoryRedirect />} />
          <Route path="database" element={<DatabaseManagementPage />} />
          <Route path="hertz" element={<HertzAutoPage />} />
          <Route path="motorq" element={<MotorqPage />} />
          <Route path="vin-inventory" element={<VinInventoryPage />} />
          <Route path="fleet-intelligence" element={<FleetIntelligencePage />} />
          <Route path="nmac-journeys" element={<NmacJourneysPage />} />
          <Route path="coco" element={<CocoPage />} />
          <Route path=":categoryId" element={<CategoryReportsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
